/**
 * SimuladorSection — cotización en seco (MVP-02C §3.7).
 *
 * Llama al mismo cotizador que usa el checkout real, sin crear carrito,
 * pedido, pago ni cotización persistida: es puramente de lectura sobre la
 * configuración actual, para comprobar que lo que se acaba de cargar
 * realmente cotiza antes de tocar el checkout de verdad.
 */
import { useState } from "react";
import { FlaskConical, Plus, Trash2 } from "lucide-react";

import { TextInput } from "../../../../../components/ui/index.js";
import { normalizarCodigoPostal } from "../../../../../domain/direcciones/api.js";
import { simulateQuote } from "../../../../../domain/shipping-admin/api.js";

const ITEM_VACIO = { weight_grams: "", quantity: 1, length_mm: "", width_mm: "", height_mm: "" };

export default function SimuladorSection() {
    const [items, setItems] = useState([{ ...ITEM_VACIO }]);
    const [postalCode, setPostalCode] = useState("28001");
    const [running, setRunning] = useState(false);
    const [result, setResult] = useState(null);

    const setItemField = (index, field, value) =>
        setItems((prev) => prev.map((it, i) => (i === index ? { ...it, [field]: value } : it)));
    const addItem = () => setItems((prev) => [...prev, { ...ITEM_VACIO }]);
    const removeItem = (index) => setItems((prev) => prev.filter((_, i) => i !== index));

    const run = async (event) => {
        event.preventDefault();
        setRunning(true);
        setResult(null);
        const payload = {
            postal_code: postalCode,
            country: "España",
            items: items.map((it) => {
                const entry = { weight_grams: Number(it.weight_grams), quantity: Number(it.quantity) || 1 };
                if (it.length_mm && it.width_mm && it.height_mm) {
                    entry.dims_mm = [Number(it.length_mm), Number(it.width_mm), Number(it.height_mm)];
                }
                return entry;
            }),
        };
        try {
            const data = await simulateQuote(payload);
            setResult(data);
        } catch {
            setResult({ quotable: false, code: "error_local", detail: "No se pudo completar la simulación." });
        } finally {
            setRunning(false);
        }
    };

    return (
        <div className="rounded-xl bg-base-100 border border-base-300 shadow-sm p-4 flex flex-col gap-6">
            <div className="flex items-start gap-2 text-xs text-base-content/60 bg-base-200 rounded-lg p-3">
                <FlaskConical className="w-4 h-4 shrink-0 text-info" />
                <p>
                    Esta prueba no crea carrito, pedido, pago ni cotización real. Solo
                    consulta la configuración actual con datos que tú introduces.
                </p>
            </div>

            <form onSubmit={run} className="flex flex-col gap-4">
                <div className="max-w-xs">
                    <label className="label py-0" htmlFor="sim-cp">
                        <span className="label-text text-xs font-semibold">Código postal de destino (prueba)</span>
                    </label>
                    <TextInput id="sim-cp" value={postalCode} inputMode="numeric"
                        onChange={(e) => setPostalCode(normalizarCodigoPostal(e.target.value))} placeholder="28001" />
                </div>

                <div className="flex flex-col gap-2">
                    <p className="text-xs uppercase tracking-widest text-base-content/50 font-semibold">
                        Artículos de prueba
                    </p>
                    {items.map((item, index) => (
                        <div key={index} className="grid grid-cols-2 sm:grid-cols-6 gap-2 items-end">
                            <div className="col-span-1">
                                <label className="label py-0 text-[11px]">Peso (g)</label>
                                <TextInput type="number" min="1" inputSize="sm" value={item.weight_grams}
                                    onChange={(e) => setItemField(index, "weight_grams", e.target.value)}
                                    aria-label={`Peso del artículo ${index + 1} en gramos`} />
                            </div>
                            <div className="col-span-1">
                                <label className="label py-0 text-[11px]">Cantidad</label>
                                <TextInput type="number" min="1" inputSize="sm" value={item.quantity}
                                    onChange={(e) => setItemField(index, "quantity", e.target.value)}
                                    aria-label={`Cantidad del artículo ${index + 1}`} />
                            </div>
                            <div className="col-span-1">
                                <label className="label py-0 text-[11px]">Largo (mm)</label>
                                <TextInput type="number" min="0" inputSize="sm" value={item.length_mm}
                                    onChange={(e) => setItemField(index, "length_mm", e.target.value)}
                                    aria-label={`Largo del artículo ${index + 1} en milímetros`} />
                            </div>
                            <div className="col-span-1">
                                <label className="label py-0 text-[11px]">Ancho (mm)</label>
                                <TextInput type="number" min="0" inputSize="sm" value={item.width_mm}
                                    onChange={(e) => setItemField(index, "width_mm", e.target.value)}
                                    aria-label={`Ancho del artículo ${index + 1} en milímetros`} />
                            </div>
                            <div className="col-span-1">
                                <label className="label py-0 text-[11px]">Alto (mm)</label>
                                <TextInput type="number" min="0" inputSize="sm" value={item.height_mm}
                                    onChange={(e) => setItemField(index, "height_mm", e.target.value)}
                                    aria-label={`Alto del artículo ${index + 1} en milímetros`} />
                            </div>
                            <button type="button" onClick={() => removeItem(index)}
                                disabled={items.length === 1}
                                className="btn btn-ghost btn-sm btn-square" aria-label={`Quitar artículo ${index + 1}`}>
                                <Trash2 className="w-4 h-4 text-error" />
                            </button>
                        </div>
                    ))}
                    <button type="button" onClick={addItem} className="btn btn-ghost btn-xs gap-1 self-start">
                        <Plus className="w-3 h-3" /> Añadir artículo
                    </button>
                </div>

                <button type="submit" className="btn btn-primary btn-sm self-start" disabled={running}>
                    {running ? "Cotizando…" : "Ejecutar cotización de prueba"}
                </button>
            </form>

            {result && (
                result.quotable ? (
                    <div role="status" className="rounded-xl border border-success/40 bg-success/5 p-4 flex flex-col gap-2">
                        <p className="font-semibold">
                            {result.shipping_cost} {result.currency}
                            <span className="text-xs font-normal text-base-content/60 ml-2">
                                (transporte {result.transport_cost} + embalaje {result.packaging_cost}, IVA {result.tax})
                            </span>
                        </p>
                        <p className="text-sm text-base-content/70">
                            Embalaje elegido: {result.packaging_profile || "—"} · Tramo{" "}
                            {result.tariff_rate_bracket ? `${result.tariff_rate_bracket[0]}-${result.tariff_rate_bracket[1]} g` : "—"}
                        </p>
                        <p className="text-sm text-base-content/70">
                            Pesos — físico {result.weights.physical_grams} g, volumétrico{" "}
                            {result.weights.volumetric_grams} g, facturable {result.weights.billable_grams} g
                        </p>
                        {result.delivery_interval?.min_date && (
                            <p className="text-sm text-base-content/70">
                                Entrega estimada: {result.delivery_interval.min_date} a {result.delivery_interval.max_date}
                            </p>
                        )}
                    </div>
                ) : (
                    <div role="alert" className="rounded-xl border border-warning/40 bg-warning/10 p-4">
                        <p className="font-semibold text-sm">No se puede cotizar ({result.code})</p>
                        <p className="text-sm text-base-content/70 mt-1">{result.detail}</p>
                    </div>
                )
            )}
        </div>
    );
}
